import { getCSSVariableValue } from '../../../../../_metronic/assets/ts/_utils'
import {
  AllTransactionsType,
  TransactionObject,
  Transaction_Type,
} from '../../../../../generated/graphql'

type InvestmentData = {
  type: Transaction_Type
  value: number
  quarter: string
  companyName: string
  roundName: string
  investorName: string
}
type IndustryData = {
  type: Transaction_Type
  value: number
  quarter: string
  companyName: string
  industry: string
}

type FundData = {
  type: Transaction_Type
  value: number
  quarter: string
  companyName: string
  investorName: string
}

type CompanyInvestmentData = {
  realizedValue: number
  unrealizedValue: number
  quarter?: string
  companyName: string
  investedValue: number
}
export function getCompanyNames(allTransactions: AllTransactionsType[]) {
  let companyNames: string[] = []
  allTransactions.forEach((comp) => {
    if (comp.companyData && !companyNames.includes(comp.companyData.name)) {
      companyNames.push(comp.companyData.name)
    }
  })

  return companyNames
}
export function getQuarters(allTransactions: AllTransactionsType[]) {
  const allCompanyTransactions: TransactionObject[] = []
  allTransactions.forEach((comp) => {
    if (comp.transactionData) {
      allCompanyTransactions.push(...(comp.transactionData as TransactionObject[]))
    }
  })
  let quarters: string[] = []
  const sortedTransactions = allCompanyTransactions.sort((a, b) =>
    a.transactionDate.localeCompare(b.transactionDate)
  )
  if (sortedTransactions.length > 0) {
    sortedTransactions.forEach((transaction) => {
      if (
        transaction.transactionType === Transaction_Type.Buy ||
        transaction.transactionType === Transaction_Type.SecondaryBuy ||
        transaction.transactionType === Transaction_Type.Sell
      ) {
        const quarter = getQuarterYear(transaction.transactionDate)
        if (!quarters.includes(`${quarter.quarter} ${quarter.year}`)) {
          quarters.push(`${quarter.quarter} ${quarter.year}`)
        }
      }
    })
  }
  return quarters
}

function getQuarterYear(dateString: string) {
  const date = new Date(dateString)
  const quarter = `Q${Math.floor(date.getMonth() / 3) + 1}` // Quarters are 1-indexed
  const year = date.getFullYear()
  return { quarter, year }
}

export type ChartData = {
  label: string
  stack: string
  data: (undefined | number)[]
  backgroundColor?: string
}

export function getInvExitsChartData(
  allTransactions: AllTransactionsType[],
  quarters: string[],
  companies: string[]
) {
  const investmentData: InvestmentData[] = []
  const industryData: IndustryData[] = []
  const companyData: CompanyInvestmentData[] = []
  let finalIndustryData: IndustryData[] = []
  const fundData: FundData[] = []
  let finalFundData: FundData[] = []
  const quarterMapping: { [key: string]: number } = {}
  const allInvestors: string[] = []
  allTransactions.forEach((company) => {
    const investorData = company.investorData
    if (investorData && investorData.length > 0) {
      const investors = investorData.map((inv) => inv?.name)
      investors.forEach((inv) => {
        if (inv && !allInvestors.includes(inv)) {
          allInvestors.push(inv)
        }
      })
    }
  })

  const fundIndex = new Map<string, number>()
  allInvestors.length > 0 &&
    allInvestors.forEach((investor) => {
      quarters.forEach((currentQuarter) => {
        const buyIdx = fundData.length
        fundData.push({
          type: Transaction_Type.Buy,
          value: 0,
          quarter: currentQuarter,
          companyName: '',
          investorName: investor,
        })
        fundIndex.set(`${currentQuarter}|${Transaction_Type.Buy}|${investor}`, buyIdx)
        const sellIdx = fundData.length
        fundData.push({
          type: Transaction_Type.Sell,
          value: 0,
          quarter: currentQuarter,
          companyName: '',
          investorName: investor,
        })
        fundIndex.set(`${currentQuarter}|${Transaction_Type.Sell}|${investor}`, sellIdx)
        quarterMapping[currentQuarter] = 0
      })
    })

  const industryIndex = new Map<string, number>()

  allTransactions.forEach((company) => {
    const currentCompanyData = {
      companyName: company.companyData.name,
      investedValue: 0,
      realizedValue: 0,
      unrealizedValue: 0,
    }
    let totalFdbShares: number = 0
    company?.investorData?.forEach((inv) => {
      totalFdbShares += inv?.fdbShares || 0
    })
    company.transactionData?.forEach((transaction) => {
      if (transaction && transaction.transactionType === Transaction_Type.Sell) {
        const quarter = getQuarterYear(transaction.transactionDate)
        currentCompanyData.realizedValue += Math.abs(transaction.amount)
        investmentData.push({
          type: transaction.transactionType,
          value: Math.abs(transaction.amount),
          quarter: `${quarter.quarter} ${quarter.year}`,
          companyName: company.companyData.name,
          roundName: 'Secondary',
          investorName: transaction.investorName || '',
        })
      } else if (
        transaction &&
        (transaction.transactionType === Transaction_Type.Buy ||
          transaction.transactionType === Transaction_Type.SecondaryBuy)
      ) {
        const quarter = getQuarterYear(transaction.transactionDate)
        currentCompanyData.investedValue += Math.abs(transaction.amount)
        investmentData.push({
          type: transaction.transactionType,
          value: Math.abs(transaction.amount),
          quarter: `${quarter.quarter} ${quarter.year}`,
          companyName: company.companyData.name,
          roundName: transaction.round || '',
          investorName: transaction.investorName || '',
        })
      }
    })
    currentCompanyData.unrealizedValue += Math.abs(
      totalFdbShares * (company?.companyData?.sharePrice || 0)
    )
    companyData.push(currentCompanyData)

    quarters.forEach((currentQuarter) => {
      const buyIdx = industryData.length
      industryData.push({
        type: Transaction_Type.Buy,
        value: 0,
        quarter: currentQuarter,
        companyName: company.companyData.name,
        industry: '',
      })
      industryIndex.set(
        `${currentQuarter}|${Transaction_Type.Buy}|${company.companyData.name}`,
        buyIdx
      )
      const sellIdx = industryData.length
      industryData.push({
        type: Transaction_Type.Sell,
        value: 0,
        quarter: currentQuarter,
        companyName: company.companyData.name,
        industry: '',
      })
      industryIndex.set(
        `${currentQuarter}|${Transaction_Type.Sell}|${company.companyData.name}`,
        sellIdx
      )
    })

    company.transactionData?.forEach((transaction) => {
      if (!transaction) return
      const quarter = getQuarterYear(transaction.transactionDate!)
      const quarterKey = `${quarter.quarter} ${quarter.year}`
      let txType: Transaction_Type | null = null
      if (transaction.transactionType === Transaction_Type.Sell) txType = Transaction_Type.Sell
      else if (
        transaction.transactionType === Transaction_Type.Buy ||
        transaction.transactionType === Transaction_Type.SecondaryBuy
      )
        txType = Transaction_Type.Buy
      if (txType === null) return
      const idx = industryIndex.get(`${quarterKey}|${txType}|${company.companyData.name}`)
      if (idx !== undefined) {
        industryData[idx].value += Math.abs(transaction.amount)
        industryData[idx].industry = company.companyData.industry || ''
      }
    })

    company.transactionData?.forEach((transaction) => {
      if (!transaction) return
      const quarter = getQuarterYear(transaction.transactionDate!)
      const quarterKey = `${quarter.quarter} ${quarter.year}`
      let txType: Transaction_Type | null = null
      if (transaction.transactionType === Transaction_Type.Sell) txType = Transaction_Type.Sell
      else if (
        transaction.transactionType === Transaction_Type.Buy ||
        transaction.transactionType === Transaction_Type.SecondaryBuy
      )
        txType = Transaction_Type.Buy
      if (txType === null) return
      const idx = fundIndex.get(`${quarterKey}|${txType}|${transaction.investorName}`)
      if (idx !== undefined) {
        fundData[idx].value += Math.abs(transaction.amount)
        fundData[idx].companyName = company.companyData.name
        fundData[idx].investorName = transaction.investorName || ''
      }
    })
  })
  finalIndustryData = industryData.filter((industry) => industry.value > 0)
  finalFundData = fundData.filter((industry) => industry.value > 0)

  const fillColors: string[] = []
  for (let i = 0; i < 10; i++) {
    const color = getCSSVariableValue('--kt-donut-' + i)
    fillColors.push(color)
  }
  const companyPositionIndex = new Map<string, number>()
  companies.forEach((name, i) => companyPositionIndex.set(name, i))
  const investedRow: number[] = Array.from({ length: companies.length }, () => 0)
  const realizedRow: number[] = Array.from({ length: companies.length }, () => 0)
  const unrealizedRow: number[] = Array.from({ length: companies.length }, () => 0)
  companyData.forEach((data) => {
    const idx = companyPositionIndex.get(data.companyName)
    if (idx === undefined) return
    if (data.investedValue > 0) investedRow[idx] += data.investedValue
    if (data.realizedValue > 0) realizedRow[idx] += data.realizedValue
    if (data.unrealizedValue > 0) unrealizedRow[idx] += data.unrealizedValue
  })
  let companySeriesData: ChartData[] = []
  if (investedRow.some((v) => v > 0)) {
    companySeriesData.push({
      label: 'Investment',
      stack: 'investment',
      data: investedRow,
      backgroundColor: '#83b5ff',
    })
  }
  if (realizedRow.some((v) => v > 0)) {
    companySeriesData.push({
      label: 'Realized value',
      stack: 'currentValue',
      data: realizedRow,
      backgroundColor: '#e45b5b',
    })
  }
  if (unrealizedRow.some((v) => v > 0)) {
    companySeriesData.push({
      label: 'Unrealized Value',
      stack: 'currentValue',
      data: unrealizedRow,
      backgroundColor: '#73c3a4',
    })
  }

  const investmentQuarterPos = new Map<string, number>()
  quarters.forEach((q, i) => investmentQuarterPos.set(q, i))
  const investmentBuyRows = new Map<string, number[]>()
  const investmentSellRows = new Map<string, number[]>()
  const investmentBuyColorPos = new Map<string, number>()
  investmentData.forEach((data) => {
    const qIdx = investmentQuarterPos.get(data.quarter)
    if (qIdx === undefined) return
    if (data.type === Transaction_Type.Buy || data.type === Transaction_Type.SecondaryBuy) {
      let row = investmentBuyRows.get(data.companyName)
      if (!row) {
        row = Array.from({ length: quarters.length }, () => 0)
        investmentBuyRows.set(data.companyName, row)
        investmentBuyColorPos.set(data.companyName, investmentBuyColorPos.size)
      }
      row[qIdx] += data.value
    } else if (data.type === Transaction_Type.Sell) {
      let row = investmentSellRows.get(data.companyName)
      if (!row) {
        row = Array.from({ length: quarters.length }, () => 0)
        investmentSellRows.set(data.companyName, row)
      }
      row[qIdx] += data.value
    }
  })
  let investmentSeriesData: ChartData[] = []
  investmentBuyRows.forEach((row, companyName) => {
    const colorIdx = investmentBuyColorPos.get(companyName) ?? 0
    investmentSeriesData.push({
      label: `${companyName}`,
      stack: 'investment',
      data: row,
      backgroundColor: fillColors[colorIdx % fillColors.length],
    })
  })
  investmentSellRows.forEach((row, companyName) => {
    investmentSeriesData.push({
      label: `${companyName} Sale`,
      stack: 'exit',
      data: row,
      backgroundColor: '#E45B5B',
    })
  })
  Object.keys(quarterMapping).forEach((key) => (quarterMapping[key] = 0))

  const industryColorsMapping: { [key: string]: string } = {}
  const fundColorsMapping: { [key: string]: string } = {}

  const quarterPositionIndex = new Map<string, number>()
  quarters.forEach((q, i) => quarterPositionIndex.set(q, i))

  const industryBuyRows = new Map<string, number[]>()
  const industrySellRow: number[] = Array.from({ length: quarters.length }, () => 0)
  finalIndustryData.forEach((data) => {
    const qIdx = quarterPositionIndex.get(data.quarter)
    if (qIdx === undefined) return
    if (data.type === Transaction_Type.Buy || data.type === Transaction_Type.SecondaryBuy) {
      const key = data.industry || 'Others'
      if (!industryColorsMapping[key]) {
        industryColorsMapping[key] =
          fillColors[Object.keys(industryColorsMapping).length % fillColors.length]
      }
      let row = industryBuyRows.get(key)
      if (!row) {
        row = Array.from({ length: quarters.length }, () => 0)
        industryBuyRows.set(key, row)
      }
      row[qIdx] += data.value
    } else if (data.type === Transaction_Type.Sell) {
      industrySellRow[qIdx] += data.value
    }
  })
  let industrySeriesData: ChartData[] = []
  industryBuyRows.forEach((row, key) => {
    industrySeriesData.push({
      label: `${key}`,
      stack: 'investment',
      data: row,
      backgroundColor: industryColorsMapping[key],
    })
  })
  if (industrySellRow.some((v) => v > 0)) {
    industrySeriesData.push({
      label: 'Exit ',
      stack: 'exit',
      data: industrySellRow,
      backgroundColor: '#E45B5B',
    })
  }

  const fundBuyRows = new Map<string, number[]>()
  const fundSellRows = new Map<string, number[]>()
  finalFundData.forEach((data) => {
    const qIdx = quarterPositionIndex.get(data.quarter)
    if (qIdx === undefined) return
    const key = data.investorName
    if (data.type === Transaction_Type.Buy || data.type === Transaction_Type.SecondaryBuy) {
      if (!fundColorsMapping[key]) {
        fundColorsMapping[key] =
          fillColors[Object.keys(fundColorsMapping).length % fillColors.length]
      }
      let row = fundBuyRows.get(key)
      if (!row) {
        row = Array.from({ length: quarters.length }, () => 0)
        fundBuyRows.set(key, row)
      }
      row[qIdx] += data.value
    } else if (data.type === Transaction_Type.Sell) {
      let row = fundSellRows.get(key)
      if (!row) {
        row = Array.from({ length: quarters.length }, () => 0)
        fundSellRows.set(key, row)
      }
      row[qIdx] += data.value
    }
  })
  let fundSeriesData: ChartData[] = []
  fundBuyRows.forEach((row, key) => {
    fundSeriesData.push({
      label: `${key}`,
      stack: 'investment',
      data: row,
      backgroundColor: fundColorsMapping[key],
    })
  })
  fundSellRows.forEach((row, key) => {
    fundSeriesData.push({
      label: `${key}`,
      stack: 'exit',
      data: row,
      backgroundColor: '#E45B5B',
    })
  })
  investmentSeriesData = investmentSeriesData.map((obj) => {
    obj.data = obj.data.map((num) => (num === 0 ? undefined : num))
    return obj
  })
  industrySeriesData = industrySeriesData.map((obj) => {
    obj.data = obj.data.map((num) => (num === 0 ? undefined : num))
    return obj
  })
  fundSeriesData = fundSeriesData.map((obj) => {
    obj.data = obj.data.map((num) => (num === 0 ? undefined : num))
    return obj
  })
  companySeriesData = companySeriesData.map((obj) => {
    obj.data = obj.data.map((num) => (num === 0 ? undefined : num))
    return obj
  })

  const portfolioData = {
    investmentSeriesData: investmentSeriesData.sort((a, b) => b.stack.localeCompare(a.stack)),
    industrySeriesData: industrySeriesData.sort((a, b) => b.stack.localeCompare(a.stack)),
    fundSeriesData: fundSeriesData.sort((a, b) => b.stack.localeCompare(a.stack)),
    companySeriesData: companySeriesData,
  }

  return portfolioData
}
